<script setup>
import { ref, computed, provide, watch, onBeforeUnmount } from 'vue';
import { Virtualizer } from 'virtua/vue';
import { useBreakpoints } from '@vueuse/core';
import { useChatListKeyboardEvents } from 'dashboard/composables/chatlist/useChatListKeyboardEvents';
import ConversationItem from './ConversationItem.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import IntersectionObserver from 'dashboard/components/IntersectionObserver.vue';

import wootConstants from 'dashboard/constants/globals';

const props = defineProps({
  conversationList: { type: Array, default: () => [] },
  isLoading: { type: Boolean, default: false },
  showEndOfListMessage: { type: Boolean, default: false },
  label: { type: String, default: '' },
  teamId: { type: [String, Number], default: 0 },
  foldersId: { type: [String, Number], default: 0 },
  conversationType: { type: String, default: '' },
  showAssignee: { type: Boolean, default: false },
  isOnExpandedLayout: { type: Boolean, default: false },
});

const emit = defineEmits(['loadMore']);

const conversationListRef = ref(null);
const virtualListRef = ref(null);
const isContextMenuOpen = ref(false);
const movedConversationDirections = ref(new Map());
const newConversationIds = ref(new Set());
const movementAnimationTimer = ref(null);
const isUserScrolling = ref(false);
const scrollIdleTimer = ref(null);
const isLoadMoreArmed = ref(false);

provide('contextMenuElementTarget', virtualListRef);

const breakpoints = useBreakpoints({
  lg: wootConstants.LARGE_SCREEN_BREAKPOINT,
});
const isLgScreen = breakpoints.greaterOrEqual('lg');
const showExpandedCards = computed(
  () => props.isOnExpandedLayout && isLgScreen.value
);

useChatListKeyboardEvents(conversationListRef);

const intersectionObserverOptions = computed(() => ({
  root: conversationListRef.value,
  rootMargin: '100px 0px 100px 0px',
}));

const onContextMenuToggle = state => {
  isContextMenuOpen.value = state;
};

const loadMoreConversations = () => {
  if (!isLoadMoreArmed.value) return;

  isLoadMoreArmed.value = false;
  emit('loadMore');
};

const clearConversationAnimations = () => {
  movedConversationDirections.value = new Map();
  newConversationIds.value = new Set();
  clearTimeout(movementAnimationTimer.value);
  movementAnimationTimer.value = null;
};

const onListScroll = () => {
  isLoadMoreArmed.value = true;

  if (!isUserScrolling.value) {
    clearConversationAnimations();
  }

  isUserScrolling.value = true;
  clearTimeout(scrollIdleTimer.value);
  scrollIdleTimer.value = setTimeout(() => {
    isUserScrolling.value = false;
    scrollIdleTimer.value = null;
  }, 180);
};

watch(
  () => props.conversationList.map(conversation => conversation.id),
  (currentIds, previousIds = []) => {
    if (!currentIds.length) {
      isLoadMoreArmed.value = false;
      return;
    }

    if (!previousIds.length) return;

    const previousIndexById = previousIds.reduce((result, id, index) => {
      result.set(id, index);
      return result;
    }, new Map());

    if (isUserScrolling.value) return;

    const newIds = currentIds.reduce((result, id) => {
      if (!previousIndexById.has(id)) result.add(id);
      return result;
    }, new Set());

    const changedPositionDirections = currentIds.reduce((result, id, index) => {
      if (!previousIndexById.has(id)) return result;

      const previousIndex = previousIndexById.get(id);
      if (previousIndex === index) return result;

      result.set(id, previousIndex > index ? 'up' : 'down');
      return result;
    }, new Map());

    const animatedItemCount = newIds.size + changedPositionDirections.size;
    if (!animatedItemCount || animatedItemCount > 8) return;

    movedConversationDirections.value = changedPositionDirections;
    newConversationIds.value = newIds;
    clearTimeout(movementAnimationTimer.value);
    movementAnimationTimer.value = setTimeout(() => {
      clearConversationAnimations();
    }, 700);
  }
);

onBeforeUnmount(() => {
  clearTimeout(movementAnimationTimer.value);
  clearTimeout(scrollIdleTimer.value);
});

provide('toggleContextMenu', onContextMenuToggle);

defineExpose({ conversationListRef });
</script>

<template>
  <div
    ref="conversationListRef"
    class="flex-1 min-h-0 overflow-y-auto conversations-list"
    :class="{ '!overflow-hidden': isContextMenuOpen }"
    @scroll.passive="onListScroll"
  >
    <Virtualizer
      ref="virtualListRef"
      v-slot="{ item }"
      :data="conversationList"
      class="[&>div:has(+_div_.active)>*]:!border-n-surface-1 [&>div:has(+_div_.selected)>*]:!border-n-surface-1"
    >
      <ConversationItem
        :source="item"
        :label="label"
        :team-id="teamId"
        :folders-id="foldersId"
        :conversation-type="conversationType"
        :show-assignee="showAssignee"
        :show-expanded="showExpandedCards"
        :position-change-direction="movedConversationDirections.get(item.id)"
        :is-new-conversation="newConversationIds.has(item.id)"
      />
    </Virtualizer>
    <div v-if="isLoading" class="flex justify-center my-4">
      <Spinner class="text-n-brand" />
    </div>
    <div
      v-else-if="showEndOfListMessage"
      class="flex items-center gap-3 px-4 py-5"
    >
      <span class="h-px flex-1 bg-n-weak" />
      <span
        class="inline-flex items-center gap-1.5 rounded-full border border-n-weak bg-n-solid-1 px-3 py-1.5 text-xs font-medium text-n-slate-10"
      >
        <Icon icon="i-lucide-check-check" class="size-3.5 text-n-teal-10" />
        {{ $t('CHAT_LIST.EOF') }}
      </span>
      <span class="h-px flex-1 bg-n-weak" />
    </div>
    <IntersectionObserver
      v-else
      :options="intersectionObserverOptions"
      @observed="loadMoreConversations"
    />
  </div>
</template>
